"use client";

import { useLingui } from "@lingui/react/macro";
import { Alert } from "@offstreetparking/design-system/feedback";
import { Button, FormCheckbox, Input } from "@offstreetparking/design-system/forms";
import { DeleteIcon } from "@offstreetparking/design-system/icons/DeleteIcon";
import { zodPlateValidation } from "@offstreetparking/domain/entities/vehicle";
import { invariant } from "@offstreetparking/domain/exceptions";
import type { CountryValue } from "@offstreetparking/geography";
import { useId } from "react";
import * as z from "zod/v4";
import type { RouterInputs } from "../../api/types";
import { VehicleInput } from "../CreatePermitWizard/components/ParkingDetailsForm/VehicleInput";
import { Form } from "../forms/Form";
const vehicleFormSchema = z.object({
	vehicles: z
		.array(
			z.object({
				plate: zodPlateValidation,
				state: z.string().min(1),
			}),
		)
		.length(1),
	nickname: z.string(),
	isDefault: z.boolean(),
});
type Vehicle = RouterInputs["parkers"]["addParkerVehicle"];
type VehicleFormProps = {
	country: CountryValue;
	defaultVehicleLabel: string;
	identityChangeWarningMessage?: string;
	initialValues: Vehicle;
	isDefaultHidden?: boolean;
	isSubmitting: boolean;
	nicknameLabel: string;
	removeIsPending?: boolean;
	removeVehicleLabel?: string;
	submitError?: string;
	onCancel: () => void;
	onSubmit: (values: Vehicle) => Promise<void>;
	onRemove?: () => void;
};
export const VehicleForm = ({
	country,
	defaultVehicleLabel,
	identityChangeWarningMessage,
	initialValues,
	isDefaultHidden = false,
	isSubmitting,
	nicknameLabel,
	onCancel,
	onSubmit,
	onRemove,
	removeIsPending = false,
	removeVehicleLabel,
	submitError,
}: VehicleFormProps) => {
	const { t } = useLingui();
	const formId = useId();
	const isExistingVehicle = !!onRemove;
	return (
		<Form
			id={formId}
			schema={vehicleFormSchema}
			initialValues={{
				vehicles: [
					{
						plate: initialValues.plate,
						state: initialValues.state,
					},
				],
				nickname: initialValues.nickname ?? "",
				isDefault: initialValues.isDefault,
			}}
			onSubmit={async (values) => {
				if (!values.vehicles[0]) {
					invariant(false, t`Please enter a valid license plate (up to 10 characters)`);
				}
				await onSubmit({
					plate: values.vehicles[0].plate,
					state: values.vehicles[0].state,
					nickname: values.nickname,
					isDefault: values.isDefault,
				});
			}}
			className="space-y-6"
		>
			{({ register, watch, formState: { errors } }) => {
				const { plate: currentPlate, state: currentState } = watch("vehicles.0");
				const hasIdentityChanged = currentPlate !== initialValues.plate || currentState !== initialValues.state;
				const showIdentityWarning = isExistingVehicle && hasIdentityChanged && !!identityChangeWarningMessage;
				return (
					<div className="space-y-8">
						{submitError && (
							<Alert
								type="error"
								title={t`Something went wrong. Please try again.`}
								message={submitError}
							/>
						)}

						<VehicleInput
							index={0}
							register={register as never}
							errors={errors as never}
							country={country}
							showRemove={false}
							onRemove={() => {}}
						/>

						<Input {...register("nickname")} autoComplete="off" label={nicknameLabel} name="nickname" />

						{!isDefaultHidden && <FormCheckbox name="isDefault" label={defaultVehicleLabel} />}

						{showIdentityWarning && <Alert type="warning" message={identityChangeWarningMessage} />}

						<div className="space-y-3">
							<Button type="submit" form={formId} className="w-full" size="lg" isLoading={isSubmitting}>
								{t`Save`}
							</Button>
							<Button
								type="button"
								intent="secondary"
								className="w-full"
								size="lg"
								onClick={onCancel}
								isDisabled={isSubmitting || removeIsPending}
							>
								{t`Cancel`}
							</Button>
						</div>

						{isExistingVehicle && (
							<div className="flex justify-center pt-2">
								<Button
									type="button"
									intent="tertiary"
									isDestructive
									leftIcon={DeleteIcon}
									onClick={() => {
										onRemove();
									}}
									isLoading={removeIsPending}
									isDisabled={isSubmitting}
								>
									{removeVehicleLabel}
								</Button>
							</div>
						)}
					</div>
				);
			}}
		</Form>
	);
};
